<template>
  <div class="fts-expression formula-expression">
    <div class="formula-row">
      <!-- The formula-bar glyph (§3.2b): marks the input as a formula editor and is the
           MOUSE door to browse-all — Ctrl+Space's pointer equivalent. -->
      <span
        class="formula-fx"
        title="Browse fields & functions (Ctrl+Space)"
        @mousedown.prevent="toggleFnBrowse"
      >fx</span>
      <!-- P3b: vue-tribute's menu retired — `#` now opens the SAME ledger in fields scope
           (fed by the same autofill resolution, inserting via the same selectTemplate HTML,
           so chips and the #-after-a-chip drill chain are byte-identical). -->
      <div
        v-html="content"
        class="formula"
        :placeholder="editorPlaceholder"
        contenteditable="true"
        @input="updateContent"
        @blur="onBlur"
        @keydown="processKeyDown"
        @keyup="onKeyUp"
        @click="updateSignatureHelp"
        @paste.prevent="onPaste($event)"
        ref="formulaExpression"
      ></div>
      <!-- lane verdict as a quiet dot at the bar's end (ruling 2026-08-10): the words moved
           into its tooltip, so a clean formula costs no status line at all -->
      <span
        v-if="engineBadge && engineVerdict && engineVerdict.engine"
        class="formula-lane-dot"
        :class="`formula-lane-dot--${engineVerdict.engine}`"
        :title="`${engineBadgeLabel} — ${engineBadgeTooltip}`"
      />
    </div>
    <!-- The one status strip (excel-formula-editor-proposal §3.2b, amended 2026-08-10):
         signature help OR the worst diagnostic — never stacked cards, marks carry the only
         colour. It renders only when it has content; the lane verdict moved to the dot on
         the editor bar. Extra diagnostics and the TEXT cheat live behind inline expanders. -->
    <div class="formula-strip" v-if="stripVisible" @mousedown.prevent>
      <template v-if="sigHelp">
        <span class="strip-sig">
          <span class="strip-sig-name">{{ sigHelp.fn.name }}</span><!--
          -->{{ signatureParts.head
          }}<span class="strip-sig-on">{{ signatureParts.active }}</span>{{ signatureParts.tail }}
        </span>
        <span class="strip-doc">— {{ sigHelp.fn.description }}</span>
        <span
          v-if="sigHelp.fn.name === 'TEXT' && sigHelp.paramIndex === 1"
          class="strip-more"
          @mousedown.prevent="stripCheatOpen = !stripCheatOpen"
        >{{ stripCheatOpen ? "▴" : "▾" }} codes</span>
      </template>
      <template v-else-if="diagnostics.length">
        <span class="strip-mark" :class="diagnostics[0].severity">{{
          diagnostics[0].severity === "error" ? "✕" : "△"
        }}</span>
        <span class="strip-text">{{ diagnostics[0].message }}</span>
        <span v-if="diagnostics[0].suggestion" class="strip-fix">{{ diagnostics[0].suggestion }}(…)?</span>
        <span
          v-if="diagnostics.length > 1"
          class="strip-more"
          @mousedown.prevent="stripExpanded = !stripExpanded"
        >{{ stripExpanded ? "▴" : "▾" }} {{ diagnostics.length - 1 }} more</span>
      </template>
    </div>
    <!-- A formula that needs the Python engine: the converter's standard spelling of it
         (gap-closure §11.4a), one click away. A checked rewrite says why it may answer
         differently on some entries. -->
    <div class="formula-strip formula-rewrite" v-if="standardRewrite" @mousedown.prevent>
      <span class="strip-text">Standard syntax:</span>
      <span class="strip-fix formula-rewrite-text">{{ standardRewrite.formula }}</span>
      <span class="strip-more formula-rewrite-use" @mousedown.prevent="useStandardRewrite">Use it</span>
    </div>
    <div class="formula-strip-open" v-if="standardRewrite && standardRewrite.kind === 'checked'">
      <div class="strip-row">
        <span class="strip-mark warn">△</span>
        <span class="strip-text">Check it gives the same answers: {{ standardRewrite.checks.join(" ") }}</span>
      </div>
    </div>
    <div class="formula-strip-open" v-if="stripExpanded && !sigHelp && diagnostics.length > 1">
      <div class="strip-row" v-for="(diag, i) in diagnostics.slice(1)" :key="'diag-' + i">
        <span class="strip-mark" :class="diag.severity">{{ diag.severity === "error" ? "✕" : "△" }}</span>
        <span class="strip-text">{{ diag.message }}</span>
      </div>
    </div>
    <div class="formula-strip-open" v-if="stripCheatOpen && sigHelp && sigHelp.fn.name === 'TEXT'">
      <div class="strip-cheat">
        <span>yy</span><span>yyyy</span><span>mm</span><span>dd</span><span>hh</span><span>ss</span><span>AM/PM</span>
        <span class="strip-cheat-note">mm = minutes beside hh/ss, month otherwise</span>
      </div>
    </div>
    <!-- <div class="editor-expression">
      {{ innerExpression }}
    </div> -->
    <!-- Function hints (docs/excel-formula-editor-proposal.md §3.3): a calm ledger — aligned
         name | signature | description rows, category labels only in browse (Ctrl+Space) mode.
         Fed by the api/CanvasTest/FormulaFunctions registry; accepting emits the canonical
         UPPERCASE spelling (§3.7b). mousedown.prevent keeps the editor's focus/selection.
         SS06025: portalled to document.body while OPEN and positioned fixed, so a host modal's
         scrolling body cannot clip it — the same escape the tribute-era menu made with
         append-to-body. It stays rendered (v-show) as the LAST child so Vue only ever patches
         it in place; a v-if here would have to re-insert it into a parent it no longer lives in.
         SS06059: closed, the node is out of the document altogether (see the fnMenu.open
         watcher) — a host mounts several editors, and parking a hidden empty menu on body for
         each made document.querySelector('.formula-fn-menu') reach a shell, not the ledger. -->
    <div
      v-show="fnMenu.open"
      class="formula-fn-menu fts-scrollbar"
      :style="{ left: fnMenu.x + 'px', top: fnMenu.y + 'px' }"
      @mousedown.prevent
      ref="fnMenuEl"
    >
      <template v-for="group in fnMenuGroups">
        <div v-if="group.category" class="fn-cat" :key="'cat-' + group.category">
          {{ group.category }}
        </div>
        <div
          v-for="item in group.items"
          :key="group.category + '-' + item.name"
          class="fn-item"
          :class="{ sel: fnMenu.items[fnMenu.sel] === item }"
          @mousedown.prevent="acceptFunction(item)"
        >
          <template v-if="item.kind === 'field'">
            <span class="fn-name">
              <span class="fn-chipname">{{ item.name }}</span>
            </span>
            <span class="fn-desc">{{
              item.variable && (item.variable.moduleId || item.variable.form || item.variable.type === 14)
                ? "chip — # again to drill"
                : "insert field chip"
            }}</span>
          </template>
          <template v-else>
            <span class="fn-name">
              {{ item.name }}<span
                class="fn-tierdot"
                :class="item.clientTier"
                :title="item.clientTier === 'client'
                  ? 'Computes on-device'
                  : item.clientTier === 'script'
                    ? 'Runs on the server script lane'
                    : 'Server native — needs the sync round trip'"
              />
            </span>
            <span class="fn-sig">{{ item.signature.slice(item.name.length) }}</span>
            <span class="fn-desc">{{ item.description }}</span>
          </template>
        </div>
      </template>
      <div class="fn-foot">
        <span>↹ insert &nbsp;·&nbsp; # fields</span>
        <span v-if="!fnMenu.browse">Ctrl+Space — browse all</span>
      </div>
    </div>
  </div>
</template>
<script>
import commons from '../commons/commons';
import { prepareVariables, filterInformation, findLastFormulaUnit } from './formula/formula';
import {
  loadFormulaFunctions,
  visibleFunctions,
  matchFunctions,
  matchFields,
  functionsFirst,
  fieldQueryAt,
  matchLevel,
  groupByCategory,
  insertionFor,
  wordAt
} from './formula/formulaFunctions';
import { diagnose, signatureAt } from './formula/formulaDiagnostics';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import formDesign from '../commons/formDesign';

export default {
  props: {
    formula: String,
    /* The host's variable scope — the level `#` opens on. SS05416: a host with no scope (the
       listing / dashboard / list-view filter designers, where the formula is a query-filter
       OPERAND evaluated once per query, not per entry) simply omits the prop; default to an
       empty level so `#` opens on the constants instead of throwing on `undefined.concat`. */
    variables: {
      type: Array,
      default: () => []
    },
    form: Object,
    autofocus: Boolean,
    template: Boolean,
    // Computed-field mode (Information design modal, formula mode): offer only the bounded native
    // subset a computed field can use (commons.computedFormula*), not the full automation/action set.
    computedField: Boolean,
    // Within-table column formula: lets the column-only tokens (rowAutoNumber) surface; at top-level
    // field level they are a no-op and stay hidden.
    columnContext: Boolean,
    // Live working information for the in-design module, used to override what findVariable returns
    // when drilling the current module's Entry root. Lets a column formula reference a table (and its
    // columns) that is still being designed and not yet committed to state.canvas. Optional.
    moduleInformation: Array,
    // Engine-indicator badge (docs/native-bundle-v4-control-formulas-proposal.md §6): classify the
    // CURRENT text live (debounced, only while this editor exists) as client / server / python via
    // api/CanvasTest/FormulaEngine. Hosts that know their design context opt in and pass it.
    engineBadge: Boolean,
    // The activity whose form this formula belongs to (schema context for classification).
    activityId: String,
    // For a table COLUMN formula: the table's display name (column('X') classifies per-row).
    parentName: String,
    /* Which design property this editor drives — "visibleFormula" / "readOnlyFormula" /
       "requiredFormula" / "formula". A control formula is badged on what the client can RESOLVE
       (it must read only fields this form carries, and never Entry, which is the saved record the
       client is never sent), not merely on whether the tokens parse. */
    propertyName: String
  },
  model: {
    prop: "formula",
    event: "updateFormula"
  },
  data: () => ({
    regex: /<fts-i-unit contenteditable="false" field="(?<fieldName>.*?)">(?<display>.*?)<\/fts-i-unit>/g,
    regex2: /<fts-i-unit field="(?<fieldName>.*?)" contenteditable="false">(?<display>.*?)<\/fts-i-unit>/g,
    formulaRegex: /#.*\W/g,
    content: "",
    /* SS05134: a `variables` refresh skipped because the editor had focus. */
    displayRefreshPending: false,
    innerExpression: "",
    engineVerdict: null,
    engineSeq: 0,
    // Function-hint state (§3.3): items is the FLAT selectable list (sel indexes into it);
    // browse = the Ctrl+Space empty-query state, rendered with category labels.
    fnRegistry: null,
    fnMenu: { open: false, browse: false, scope: "mixed", items: [], groups: [], sel: 0, x: 0, y: 0 },
    // viewport rect of the caret the open menu is anchored to — kept so a scroll of the
    // host modal (or a resize) can re-place the portalled menu without a fresh keystroke
    fnAnchor: null,
    // Status-strip state (§3.2b): signature help follows the caret; the expanders hold the
    // overflow diagnostics and the TEXT format cheat.
    sigHelp: null,
    stripExpanded: false,
    stripCheatOpen: false,
    // guards stale async field-level resolutions (autofill awaits module lookups)
    fnFieldSeq: 0
  }),
  methods: {
    debounceInput: _.debounce(function() {
      this.$emit("change");
    }, 800),
    /* Engine badge: classify the current text against the saved canvas (debounced per edit — no
       polling; the component only exists while its editor is open). A null engine (mid-typing /
       unresolvable) clears the badge rather than showing a wrong one. */
    debounceClassifyEngine: _.debounce(function() {
      this.classifyEngine();
    }, 600),
    async classifyEngine() {
      if (!this.engineBadge) return;
      const formula = this.innerExpression || this.formula || "";
      const seq = ++this.engineSeq;
      if (!formula.trim()) {
        this.engineVerdict = null;
        return;
      }
      try {
        const res = await canvasDesignStore.dispatch("classifyFormulaEngine", {
          activityId: this.activityId || null,
          formula,
          parentName: this.parentName || null,
          propertyName: this.propertyName || null
        });
        if (seq === this.engineSeq) {
          this.engineVerdict = res && res.data ? res.data : null;
        }
      } catch (err) {
        // advisory only — never surface an error for a badge
        if (seq === this.engineSeq) this.engineVerdict = null;
      }
    },
    /* Replace the text with the converter's standard spelling, as if the user had typed it. */
    async useStandardRewrite() {
      const rewrite = this.standardRewrite;
      if (!rewrite) return;
      this.innerExpression = rewrite.formula;
      this.$emit("updateFormula", rewrite.formula);
      this.$emit("input", rewrite.formula);
      this.$emit("change", rewrite.formula);
      await this.updateDisplay(rewrite.formula);
      this.classifyEngine();
    },
    processKeyDown(evt) {
      // Function-hint menu (§3.3). Ctrl+Space opens the browse (empty-query) state; while
      // the menu is open it owns the navigation keys. Runs BEFORE the legacy handlers so
      // Enter accepts a suggestion instead of being swallowed.
      if (evt.ctrlKey && (evt.code === "Space" || evt.key === " " || evt.keyCode === 32)) {
        evt.preventDefault();
        this.openFnBrowse();
        return;
      }
      if (this.fnMenu.open) {
        // match on evt.key (keyCode is deprecated and synthetic events may send 0)
        if (evt.key === "ArrowDown" || evt.keyCode === 40) {
          evt.preventDefault();
          this.moveFnSel(1);
          return;
        }
        if (evt.key === "ArrowUp" || evt.keyCode === 38) {
          evt.preventDefault();
          this.moveFnSel(-1);
          return;
        }
        if (evt.key === "Enter" || evt.key === "Tab"
          || evt.keyCode === 13 || evt.keyCode === 9) {
          evt.preventDefault();
          this.acceptFunction(this.fnMenu.items[this.fnMenu.sel]);
          return;
        }
        if (evt.key === "Escape" || evt.keyCode === 27) {
          evt.preventDefault();
          this.closeFnMenu();
          return;
        }
      }

      if (evt.keyCode == 8) {
        if (
          evt.target.lastChild &&
          evt.target.lastChild.nodeName == "#text" &&
          evt.target.lastChild.wholeText == " "
        ) {
          if (
            evt.target.lastChild.previousElementSibling &&
            evt.target.lastChild.previousElementSibling.nodeName == "FTS-I-UNIT"
          ) {
            evt.target.removeChild(evt.target.lastChild.previousElementSibling);
          }
        }
      }

      if (!this.template && evt.keyCode == 13) {
        evt.preventDefault();
      }
    },
    focus() {
      this.$refs.formulaExpression.focus();
    },
    /* ---- function hints (docs/excel-formula-editor-proposal.md §3.3, §3.5b) ------------ */
    /* The registry filtered for this host (D5/D8 dedup in visibleFunctions): computed mode
       keeps only what the DEVICE computes — which truthfully includes the native client
       tier (iif/sum/hasChange), not just the Excel one. rowAutoNumber is per-ROW: outside a
       table column it is a no-op, same rule as its chip constant (columnOnly). */
    fnCandidates() {
      const visible = visibleFunctions(this.fnRegistry, { computedField: this.computedField });
      return this.columnContext
        ? visible
        : visible.filter(f => f.name !== "rowAutoNumber");
    },
    /* Chip candidates for the unified typed menu and the browse Fields/Constants groups:
       the host's variables plus the mode's chip constants (TODAY/NOW/CURRENT USER — the
       chip spellings the D5 ruling teaches instead of the function ones). */
    fnConstants() {
      if (this.computedField) {
        return commons.computedFormulaConstants.filter(x => this.columnContext || !x.columnOnly);
      }
      /* Query-filter operand (SS05416): QueryFilterProcessor.processFormula resolves the
         formula ONCE per query with no variable payload, so only the constants it can answer
         on its own are offered. CURRENT USER is left out on purpose — it converts to a bare
         `CurrentUser` name the listing query never binds (a user field is matched with the
         operand's own "me" instead). */
      return this.filterContext
        ? commons.formulaConstants.filter(x => !x.fieldName.startsWith("CURRENTUSER"))
        : commons.formulaConstants;
    },
    fnChipCandidates() {
      return (this.variables || []).concat(this.fnConstants());
    },
    /* Chip-stripped text between the start of the editor and the caret. Chips become a
       single space — their labels carry apostrophes ('s path indicators) that would poison
       wordAt's open-string-literal heuristic. Null when the caret is not in this editor. */
    fnPrefixText() {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;
      const range = selection.getRangeAt(0);
      if (!range.collapsed) return null;
      const editor = this.$refs.formulaExpression;
      if (!editor || !editor.contains(range.startContainer)) return null;

      let text = "";
      let reached = false;
      const visit = node => {
        if (reached) return;
        if (node === range.startContainer) {
          if (node.nodeType === Node.TEXT_NODE) {
            text += node.data.slice(0, range.startOffset);
          } else {
            for (let i = 0; i < range.startOffset && i < node.childNodes.length; i++) {
              visit(node.childNodes[i]);
            }
          }
          reached = true;
          return;
        }
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.data;
          return;
        }
        if (node.nodeName === "FTS-I-UNIT") {
          if (node.contains(range.startContainer)) reached = true;
          else text += " ";
          return;
        }
        for (const child of node.childNodes) {
          if (reached) break;
          visit(child);
        }
      };
      visit(editor);
      // contenteditable inserts non-breaking spaces; normalize for the word regex
      return text.replace(/ /g, " ");
    },
    /* Sync with the text on every input: open/refresh on a 2+ letter word, close otherwise.
       Browse mode is sticky until a key or click dismisses it. */
    updateFnMenu() {
      // typing exits the sticky browse state into the normal filtered flow
      this.fnMenu.browse = false;
      const prefix = this.fnPrefixText();

      // Fields scope (P3b — one dropdown): a `#` before the caret. Fed by the tribute-era
      // autofill resolution — the root list, or the LAST CHIP's children when the '#'
      // follows a chip (the drill chain) — so what the menu offers is exactly what the
      // old menu offered, in the ledger's shell. Templates are filtered out: the function
      // rows own those concepts (D5/D7).
      const fieldQuery = prefix === null ? null : fieldQueryAt(prefix);
      if (fieldQuery !== null) {
        const seq = ++this.fnFieldSeq;
        this.autofill(fieldQuery, this, list => {
          if (seq !== this.fnFieldSeq) return; // a newer keystroke superseded this level
          const items = matchLevel(
            (list || []).filter(x => x && x.name && !x.template),
            fieldQuery
          ).map(v => ({ kind: "field", name: v.name, variable: v }));
          if (!items.length) {
            this.closeFnMenu();
            return;
          }
          this.fnMenu.scope = "fields";
          this.fnMenu.items = items;
          this.fnMenu.groups = [];
          this.fnMenu.sel = Math.min(this.fnMenu.sel, items.length - 1);
          this.fnMenu.open = true;
          this.positionFnMenu();
        });
        return;
      }

      this.fnMenu.scope = "mixed";
      const word = prefix === null ? null : wordAt(prefix);
      if (!word) {
        this.closeFnMenu();
        return;
      }
      // §3.5b: one query matches everything — field chips AND functions. D6 ranking:
      // fields first, unless the query is typed ALL-UPPERCASE (function intent).
      const fields = matchFields(this.fnChipCandidates(), word)
        .map(v => ({ kind: "field", name: v.name, variable: v }));
      const functions = matchFunctions(this.fnCandidates(), word);
      const items = functionsFirst(word)
        ? functions.concat(fields)
        : fields.concat(functions);
      if (!items.length) {
        this.closeFnMenu();
        return;
      }
      this.fnMenu.items = items;
      this.fnMenu.groups = [];
      this.fnMenu.sel = Math.min(this.fnMenu.sel, items.length - 1);
      this.fnMenu.open = true;
      this.positionFnMenu();
    },
    /* The fx glyph's click: toggle browse-all. Focus the editor first so the menu anchors
       to a caret INSIDE it (mousedown.prevent keeps the click from blurring anything). */
    toggleFnBrowse() {
      if (this.fnMenu.open) {
        this.closeFnMenu();
        return;
      }
      this.focus();
      this.openFnBrowse();
    },
    openFnBrowse() {
      // browse-all, grouped: Fields · Constants · function categories. Only leaf variables
      // — containers (module refs, activity forms) wait for the P3b drill-down.
      const groups = [];
      const leaves = (this.variables || [])
        .filter(v => v && v.name && !v.moduleId && !v.form)
        .map(v => ({ kind: "field", name: v.name, variable: v }));
      if (leaves.length) groups.push({ category: "Fields", items: leaves });
      const constants = this.fnConstants()
        .map(v => ({ kind: "field", name: v.name, variable: v }));
      if (constants.length) groups.push({ category: "Constants", items: constants });
      groups.push(...groupByCategory(this.fnCandidates()));

      // flatten in group order so arrow-key traversal matches the rendered ledger
      const items = groups.reduce((all, group) => all.concat(group.items), []);
      if (!items.length) return;
      this.fnMenu.groups = groups;
      this.fnMenu.items = items;
      this.fnMenu.sel = 0;
      this.fnMenu.browse = true;
      this.fnMenu.open = true;
      this.positionFnMenu();
    },
    closeFnMenu() {
      this.fnMenu.open = false;
      this.fnMenu.browse = false;
      this.fnMenu.scope = "mixed";
      this.fnMenu.items = [];
      this.fnMenu.groups = [];
      this.fnMenu.sel = 0;
    },
    moveFnSel(step) {
      const count = this.fnMenu.items.length;
      if (!count) return;
      this.fnMenu.sel = (this.fnMenu.sel + step + count) % count;
      this.$nextTick(() => {
        const menu = this.$refs.fnMenuEl;
        const selected = menu && menu.querySelector(".fn-item.sel");
        if (selected && selected.scrollIntoView) selected.scrollIntoView({ block: "nearest" });
      });
    },
    /* Menu sits under the caret in VIEWPORT coordinates: the element is portalled to
       document.body (portalFnMenu), so it is positioned fixed against the viewport — no
       scrolling modal body can clip it and no transformed ancestor can displace it. */
    positionFnMenu() {
      let rect = null;
      const selection = window.getSelection();
      const editor = this.$refs.formulaExpression;
      // only anchor to the caret when it is actually inside THIS editor (the fx-glyph
      // path can open the menu before any caret exists here)
      if (selection && selection.rangeCount
        && editor && editor.contains(selection.getRangeAt(0).startContainer)) {
        rect = selection.getRangeAt(0).getBoundingClientRect();
      }
      if (!rect || (!rect.width && !rect.height && !rect.top && !rect.left)) {
        rect = editor ? editor.getBoundingClientRect() : this.$el.getBoundingClientRect();
      }
      this.fnAnchor = { left: rect.left, top: rect.top, bottom: rect.bottom };
      this.fnMenu.x = Math.round(rect.left);
      this.fnMenu.y = Math.round(rect.bottom) + 4;
      // second pass once the rows are laid out (the menu is measurable only after it is
      // shown): keep the whole ledger on screen — clamp to the right edge, flip above the
      // caret when there is no room below.
      this.$nextTick(this.clampFnMenu);
    },
    clampFnMenu() {
      const menu = this.$refs.fnMenuEl;
      if (!menu || !this.fnMenu.open || !this.fnAnchor) return;
      const margin = 8;
      const width = menu.offsetWidth;
      const height = menu.offsetHeight;
      const x = Math.max(margin, Math.min(
        Math.round(this.fnAnchor.left),
        window.innerWidth - width - margin
      ));
      let y = Math.round(this.fnAnchor.bottom) + 4;
      if (y + height > window.innerHeight - margin) {
        const above = Math.round(this.fnAnchor.top) - height - 4;
        y = above >= margin ? above : Math.max(margin, window.innerHeight - height - margin);
      }
      this.fnMenu.x = x;
      this.fnMenu.y = y;
    },
    /* The menu lives on document.body, so an ancestor scroll (the modal body) or a resize
       moves the editor out from under it — re-anchor to the caret instead of drifting. */
    onViewportChange() {
      if (this.fnMenu.open) this.positionFnMenu();
    },
    /* SS06025: lift the menu out of the component so no host's overflow can clip it.
       SS06059: done on OPEN rather than once on mount — a design host mounts several editors
       (the Required/Editable/Visible lanes), and an eager portal parked a hidden, empty menu
       on body for each of them, so document.querySelector('.formula-fn-menu') reached a shell
       instead of the open ledger. The node itself is still never re-created (v-show), so Vue
       keeps patching this same element wherever it currently lives. */
    portalFnMenu() {
      const menu = this.$refs.fnMenuEl;
      if (menu && menu.parentNode !== document.body) document.body.appendChild(menu);
    },
    /* SS06059: out of the document entirely while closed — not back into .fts-expression,
       which would put it under the host modal's clip again the moment it reopened. */
    unportalFnMenu() {
      const menu = this.$refs.fnMenuEl;
      if (menu && menu.parentNode) menu.parentNode.removeChild(menu);
    },
    /* Accept: replace the typed word (none in browse mode) with the insertion — the
       canonical function call with the caret inside the parens (D1: canonical on accept
       only, typed text never silently rewritten), or the field CHIP for kind:"field" rows
       (the same fts-i-unit HTML the tribute path emits — serialization unchanged). */
    acceptFunction(item) {
      if (!item) return;
      const editor = this.$refs.formulaExpression;
      editor.focus();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);

      // Fields scope (P3b): insert through selectTemplate — the SAME HTML builder the
      // tribute menu used, so chips (and the chained parent's-child form) stay
      // byte-identical. Call it BEFORE deleting the '#query' trigger: the chained branch
      // reads the caret's '#…' text node to find and remove the parent chip.
      if (this.fnMenu.scope === "fields" && item.kind === "field") {
        const html = this.selectTemplate({ original: item.variable });
        const prefix = this.fnPrefixText();
        const query = prefix === null ? null : fieldQueryAt(prefix);
        if (query !== null && range.startContainer.nodeType === Node.TEXT_NODE
          && range.startOffset >= query.length + 1) {
          range.setStart(range.startContainer, range.startOffset - (query.length + 1));
          range.deleteContents();
        }
        const container = document.createElement("span");
        container.innerHTML = html;
        let last = null;
        while (container.firstChild) {
          const node = container.firstChild;
          range.insertNode(node);
          range.setStartAfter(node);
          last = node;
        }
        if (last) {
          const caret = document.createRange();
          caret.setStartAfter(last);
          caret.collapse(true);
          selection.removeAllRanges();
          selection.addRange(caret);
        }
        this.closeFnMenu();
        this.updateContent({ target: editor });
        return;
      }

      if (!this.fnMenu.browse && range.startContainer.nodeType === Node.TEXT_NODE) {
        const prefix = this.fnPrefixText();
        const word = prefix === null ? null : wordAt(prefix);
        if (word && range.startOffset >= word.length) {
          range.setStart(range.startContainer, range.startOffset - word.length);
          range.deleteContents();
        }
      }

      if (item.kind === "field") {
        const variable = item.variable;
        const container = document.createElement("span");
        container.innerHTML =
          `<fts-i-unit contenteditable="false" field="${variable.fieldName}">` +
          commons.informationIconHtml(variable, this.$store.getters.workspaceModules) +
          `&nbsp;<span class="variable">${variable.name}</span></fts-i-unit> `;
        let last = null;
        while (container.firstChild) {
          const node = container.firstChild;
          range.insertNode(node);
          range.setStartAfter(node);
          last = node;
        }
        const chipCaret = document.createRange();
        chipCaret.setStartAfter(last);
        chipCaret.collapse(true);
        selection.removeAllRanges();
        selection.addRange(chipCaret);
        this.$emit("lastVariable", variable);
      } else {
        const insertion = insertionFor(item);
        const textNode = document.createTextNode(insertion.text);
        range.insertNode(textNode);
        const caret = document.createRange();
        caret.setStart(textNode, insertion.caretOffset);
        caret.collapse(true);
        selection.removeAllRanges();
        selection.addRange(caret);
      }

      this.closeFnMenu();
      this.updateContent({ target: editor });
    },
    onPaste(evt) {
      var text = evt.clipboardData.getData("text/plain");
      document.execCommand("insertHTML", false, text);
    },
    onBlur() {
      // menu/strip clicks never blur (mousedown.prevent) — a real focus loss dismisses hints
      this.closeFnMenu();
      /* SS05134: a refresh held back to protect the caret runs now. */
      if (this.displayRefreshPending) {
        this.displayRefreshPending = false;
        this.updateDisplay(this.formula);
      }
      this.sigHelp = null;
      this.stripCheatOpen = false;
      this.$emit("blur");
    },
    onKeyUp() {
      this.debounceInput();
      this.updateSignatureHelp();
    },
    /* Signature help follows the caret (keyup + click — keydown fires before the caret
       moves). Cheat stays open only while the SAME call is active. */
    updateSignatureHelp() {
      const prefix = this.fnPrefixText();
      const help = prefix === null
        ? null
        : signatureAt(prefix, this.fnRegistry || []);
      if (!help || !this.sigHelp || help.fn.name !== this.sigHelp.fn.name) {
        this.stripCheatOpen = false;
      }
      this.sigHelp = help;
    },
    /* When drilling the current in-design module's Entry root, prefer the host-supplied working
       information (which can include a table still being designed and not yet committed to
       state.canvas) over findVariable's snapshot. Falls back to the snapshot for other modules. */
    moduleInformationFor(lastVariable, data) {
      const isCurrentModule =
        canvasDesignStore.state.vectorId &&
        lastVariable && lastVariable.moduleId &&
        lastVariable.moduleId.toString() == canvasDesignStore.state.vectorId.toString();
      return isCurrentModule && Array.isArray(this.moduleInformation)
        ? this.moduleInformation
        : (data && data.information);
    },
    getModule(id) {
      if (id && canvasDesignStore.getters.workspaceModules) {
        const findModule = canvasDesignStore.getters.workspaceModules.find(
          x => x.id == id
        );
        if (findModule)
          return {
            id: findModule.id,
            name: findModule.name,
            emoji: findModule.emoji
          };
      }
    },
    updateContent(evt) {
      if (!evt.target) {
        console.error("event target is null or undefined");
        return;
      } else if (!(evt.target instanceof HTMLElement)) {
        console.error("event target is not an HTMLElement");
        return;
      }

      if (
        evt.target.childNodes.length === 1 &&
        (
          evt.target.lastChild.nodeName === "BR" ||
          /^<div><br><\/div>$/.test(evt.target.innerHTML)
        )
      ) {
        evt.target.removeChild(evt.target.lastChild);
      }

      if (!this.template) {
        if (evt.target.lastChild) {
          if (evt.target.lastChild.nodeName === "BR") {
            evt.target.removeChild(evt.target.lastChild);
          } else if (
            evt.target.lastChild.previousElementSibling &&
            evt.target.lastChild.previousElementSibling.nodeName === "BR"
          ) {
            evt.target.removeChild(evt.target.lastChild.previousElementSibling);
          }
        }

        if (!(evt.target.innerHTML && evt.target.innerHTML.trim())) {
          evt.target.innerHTML = "";
        }
      }

      let formulaText = evt.target.innerHTML.match(this.regex2) ?
        evt.target.innerHTML.replace(this.regex2, "{{$1}}") :
        evt.target.innerHTML.replace(this.regex, "{{$1}}");

      formulaText = formulaText.replaceAll("&nbsp;", " ");
      // formulaText = formulaText.replaceAll("<br>", "");
      formulaText = formulaText.replaceAll("&gt;", ">");
      formulaText = formulaText.replaceAll("&lt;", "<");
      formulaText = formulaText.replaceAll("&amp;", "&");
      this.innerExpression = formulaText;
      this.$emit("updateFormula", formulaText);
      this.$emit("input", formulaText);
      this.$emit("change", formulaText);
      if (this.engineBadge) this.debounceClassifyEngine();
      this.updateFnMenu();
      this.updateSignatureHelp();
    },
    async autofill(text, vm, cb) {
      var rangeStart = window.getSelection().getRangeAt(0).startContainer;
      if (
        rangeStart &&
        rangeStart.data &&
        rangeStart.data.indexOf("#") == 0 &&
        findLastFormulaUnit(rangeStart)
      ) {
        let lastUnit = findLastFormulaUnit(rangeStart);
        this.regex.lastIndex = 0;
        this.regex2.lastIndex = 0;

        let result = this.regex.exec(lastUnit.outerHTML);
        if (!result) {
          result = this.regex2.exec(lastUnit.outerHTML);
        }

        if (result) {
          let variablePaths = result.groups.fieldName.split(".");
          let variableValues = [];
          let source = this.variables;
          let lastVariable = null;
          for (let variablePath of variablePaths) {
            //console.log("path:" + variablePath);
            lastVariable = source.find(x => x.fieldName == variablePath);
            variableValues.push(lastVariable);

            if (lastVariable && lastVariable.form) {
              source = await prepareVariables({
                information: filterInformation(
                  commons.getAllElements(null, lastVariable.form)
                ),
                variableType: lastVariable.type,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            } else if (lastVariable && lastVariable.moduleId) {
              let data = await canvasDesignStore.dispatch("findVariable", {
                moduleId: lastVariable.moduleId
              });

              if (lastVariable.informationId) {
                let table = data.information.find(
                  x => x.id == lastVariable.informationId
                );
                source = await prepareVariables({
                  information: table.fields,
                  variableType: lastVariable.variableType ?
                    lastVariable.variableType :
                    lastVariable.type
                  ,
                  modules: canvasDesignStore.getters.workspaceModules,
                  moduleId: lastVariable.moduleId,
                  activityId: lastVariable.activityId
                });
              } else if (lastVariable.activityId) {
                if (lastVariable.name == "Form" && this.form) {
                  source = await prepareVariables({
                    information: filterInformation(commons.getAllElements(null, this.form)),
                    variableType: lastVariable.type,
                    modules: canvasDesignStore.getters.workspaceModules,
                    moduleId: lastVariable.moduleId,
                    activityId: lastVariable.activityId
                  });
                } else if (
                  lastVariable.activityId == "createEditView" ||
                  lastVariable.activityId == "create" ||
                  lastVariable.activityId == "edit"
                ) {
                  const classification = formDesign.getDefaultClassificationByActivityId(
                    data.standards,
                    lastVariable.activityId,
                    data.information
                  );
                  source = await prepareVariables({
                    information: filterInformation(classification),
                    variableType: lastVariable.type,
                    modules: canvasDesignStore.getters.workspaceModules,
                    moduleId: lastVariable.moduleId,
                    activityId: lastVariable.activityId
                  });
                } else {
                  source = await prepareVariables({
                    information: filterInformation(
                      data.activities.find(x => x.id == lastVariable.activityId)
                        .classification
                    ),
                    variableType: lastVariable.type,
                    modules: canvasDesignStore.getters.workspaceModules,
                    moduleId: lastVariable.moduleId,
                    activityId: lastVariable.activityId
                  });
                }
              } else {
                source = await prepareVariables({
                  information: this.moduleInformationFor(lastVariable, data),
                  variableType: lastVariable.type,
                  modules: canvasDesignStore.getters.workspaceModules,
                  moduleId: lastVariable.moduleId,
                  activityId: lastVariable.activityId
                });
              }
            } else if (lastVariable.referenceModuleTableId) {
              let referenceModule = await canvasDesignStore.dispatch("findVariable", {
                moduleId: lastVariable.referenceModuleId
              });

              let referenceTable = referenceModule.information.find(
                x => x.id == lastVariable.referenceModuleTableId
              );

              source = await prepareVariables({
                information: referenceTable.fields,
                variableType: lastVariable.variableType
                  ? lastVariable.variableType
                  : lastVariable.type
                ,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            }

            if (lastVariable && lastVariable.type == 14) {
              source = await prepareVariables({
                information: lastVariable.fields,
                variableType: lastVariable.type,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            }
          }

          if (lastVariable && lastVariable.type == 14) {
            source = await prepareVariables({
              information: lastVariable.fields,
              variableType: lastVariable.type,
              modules: canvasDesignStore.getters.workspaceModules,
              moduleId: lastVariable.moduleId,
              activityId: lastVariable.activityId
            });
            source.forEach(x => {
              x.parent = result.groups.fieldName;
              x.parents = variableValues;
              x.tableField = true;
            });
            cb(source);
          } else if (lastVariable && lastVariable.moduleId) {
            source.forEach(x => {
              x.parent = result.groups.fieldName;
              x.parents = variableValues;
            });
            cb(source);
          } else if (lastVariable && lastVariable.referenceModuleTableId) {
            source.forEach(x => {
              x.parent = result.groups.fieldName;
              x.parents = variableValues;
            });
            cb(source);
          } else {
            this.variables.forEach(variable => {
              if (variable.moduleId) {
                variable.moduleName = vm.getModule(variable.moduleId).name;
              }
            });

            cb(this.variables);
          }
        }
      } else {
        // computed fields support only the native subset, and a scope-less filter host only the
        // constants its query engine resolves — fnConstants() owns that choice for both the typed
        // `#` level and the Ctrl+Space browse groups. Column-only tokens (Row Auto Number, per-row
        // and a no-op at top-level field level) stay filtered out the same way.
        let selection = _.cloneDeep(this.selection);
        selection = selection.concat(this.fnConstants());
        selection = selection.concat(
          this.computedField
            ? commons.computedFormulaTemplates.filter(x => this.columnContext || !x.columnOnly)
            : commons.formulaTemplates
        );

        for (const variable of selection) {
          if (variable.moduleId) {
            let data = await canvasDesignStore.dispatch("findVariable", {
              moduleId: variable.moduleId
            });
            variable.moduleName = data.name;
            if (variable.variableType) {
              variable.type = variable.variableType;
            }
            if (variable.informationId) {
              variable.information = data.information.find(
                x => x.id == variable.informationId
              );
            }

            if (variable.activityId) {
              variable.activity = data.standards.find(
                x => x.id == variable.activityId
              );
              if (!variable.activity) {
                variable.activity = data.activities.find(
                  x => x.id == variable.activityId
                );
              }
            }

            if (variable.variableType) {
              variable.type = variable.variableType;
            }
          }
        }

        cb(selection);
      }
    },
    selectTemplate(item) {
      let inMiddle = false;
      let selectionNode = window.getSelection();

      // if (selectionNode && selectionNode.anchorNode) {
      //   console.log(selectionNode.anchorNode);
      // }

      // if (
      //   selectionNode &&
      //   selectionNode.anchorNode &&
      //   selectionNode.anchorNode.nextSibling &&
      //   selectionNode.anchorNode.nextSibling.wholeText
      // ) {
      //   console.log(selectionNode.anchorNode.nextSibling.wholeText);
      // }

      if (selectionNode && selectionNode.anchorNode.nextSibling) {
        if (
          selectionNode.anchorNode.nextSibling.wholeText &&
          selectionNode.anchorNode.nextSibling.wholeText.match(
            this.formulaRegex
          )
        ) {
          inMiddle = true;
          console.log("in middle");
        } else if (
          selectionNode.anchorNode.nextSibling.nodeName == "FTS-I-UNIT" ||
          selectionNode.anchorNode.nextSibling.nodeName == "BR" ||
          (selectionNode.anchorNode.nextSibling.nextSibling &&
            selectionNode.anchorNode.nextSibling.nextSibling.nodeName == "BR")
        ) {
          inMiddle = true;
          console.log("in middle");
        }
      }

      if (item.original.template) {
        return `${item.original.template}`;
      } else if (item.original.function) {
        return `.${item.original.fieldName}(`;
      } else if (item.original.parent != null) {
        var rangeStart = window.getSelection().getRangeAt(0).startContainer;
        if (
          rangeStart &&
          rangeStart.data &&
          rangeStart.data.indexOf("#") == 0
        ) {
          let lastUnit = findLastFormulaUnit(rangeStart);
          if (lastUnit) {
            lastUnit.remove();
          }
        }

        var variablePaths = `${item.original.parent}.${item.original.fieldName}`.split(
          "."
        );
        var variableValues = [];
        var source = this.variables;
        for (let variablePath of variablePaths) {
          var variable = source.find(x => x.fieldName == variablePath);
          variableValues.push(variable);
        }

        this.$emit("lastVariable", item.original);

        return (
          `<fts-i-unit contenteditable="false" field="${item.original.parent}.${
            item.original.fieldName
          }">${commons.informationIconHtml(
            item.original,
            this.$store.getters.workspaceModules
          )}&nbsp;${item.original.parents
            .map(x => `<span class="variable">${x.name}</span>`)
            .join(
              "<span class='path-indicator'>'s</span>&nbsp;"
            )}<span class='path-indicator'>'s</span>&nbsp; <span class="variable">${
            item.original.name
          }</span></fts-i-unit>` +
          (inMiddle ? "" : " ") +
          (!item.original.moduleId && item.original.type != 14 ? " " : "")
        );
      }

      this.$emit("lastVariable", item.original);
      let elementResult =
        `<fts-i-unit contenteditable="false" field="${
          item.original.fieldName
        }">${commons.informationIconHtml(
          item.original,
          this.$store.getters.workspaceModules
        )}&nbsp;<span class="variable">${
          item.original.name
        }</span></fts-i-unit>` + (inMiddle ? "" : " "); // + (item.original.moduleId ? "" : " ");

      return elementResult;
    },
    /* True while the caret is actually in this editor. */
    editorHasFocus() {
      const editor = this.$refs && this.$refs.formulaExpression;
      return !!editor && typeof document !== "undefined" && document.activeElement === editor;
    },
    async updateDisplay(formula) {
      let re = /{{(?<fieldName>[\w|\.]*?)}}/g;

      try {
        let match = re.exec(formula);
        this.content = formula;
        let selection = _.cloneDeep(this.variables);
        // include the computed-field constants too, so a saved {{ROWAUTONUMBER}} (or TODAY/NOW)
        // resolves back into its chip when an existing computed formula is re-opened.
        selection = selection.concat(commons.formulaConstants, commons.computedFormulaConstants);

        while (match != null) {
          let variablePaths = match[1].split(".");
          let variableValues = [];
          let source = selection;
          let lastVariable,
            previousVariable = null;
          for (let variablePath of variablePaths) {
            previousVariable = lastVariable;
            lastVariable = source.find(x => x.fieldName == variablePath);

            if (
              !lastVariable &&
              previousVariable &&
              previousVariable.type == 14
            ) {
              source = await prepareVariables({
                information: previousVariable.fields,
                variableType: previousVariable.type,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: previousVariable.moduleId,
                activityId: previousVariable.activityId
              });
              lastVariable = source.find(x => x.fieldName == variablePath);
            }

            variableValues.push(lastVariable);

            // {{query.code}} / {{event.items}} (automation-blocks-simplification P9): the rest are free keys
            if (commons.isLenientPayloadChip(variableValues)) {
              variablePaths.slice(1).forEach(key => variableValues.push({ name: key, fieldName: key }));
              break;
            }

            // a constant with fields of its own ({{CURRENTUSER.DisplayName}}): the next segment is one of them
            if (lastVariable && lastVariable.constantFields) {
              source = lastVariable.constantFields;
              continue;
            }

            /* a form-scoped variable ("Form") expands from its form design, not from the
               module's information — Control/Input elements only exist on the design, so
               resolving them through module information loses them and the chip falls back
               to raw {{form.xxx}} text. Same source the hint menu (autofill) walks. */
            if (
              lastVariable &&
              (lastVariable.form || (lastVariable.name == "Form" && this.form))
            ) {
              source = await prepareVariables({
                information: filterInformation(
                  commons.getAllElements(null, lastVariable.form || this.form)
                ),
                variableType: lastVariable.type,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            } else if (lastVariable && lastVariable.moduleId) {
              let data = await canvasDesignStore.dispatch("findVariable", {
                moduleId: lastVariable.moduleId
              });

              if (lastVariable.informationId) {
                source = await prepareVariables({
                  information: data.information.find(
                    x => x.id == lastVariable.informationId
                  )["fields"],
                  variableType: lastVariable.type,
                  modules: canvasDesignStore.getters.workspaceModules,
                  moduleId: lastVariable.moduleId,
                  activityId: lastVariable.activityId
                });
              } else {
                source = await prepareVariables({
                  information: this.moduleInformationFor(lastVariable, data),
                  variableType: lastVariable.type,
                  modules: canvasDesignStore.getters.workspaceModules,
                  moduleId: lastVariable.moduleId,
                  activityId: lastVariable.activityId
                });
              }
            } else if (lastVariable && lastVariable.type == 14) {
              source = await prepareVariables({
                information: lastVariable.fields,
                variableType: lastVariable.type,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            } else if (lastVariable && lastVariable.referenceModuleTableId) {
              let referenceModule = await canvasDesignStore.dispatch("findVariable", {
                moduleId: lastVariable.referenceModuleId
              });

              let referenceTable = referenceModule.information.find(
                x => x.id == lastVariable.referenceModuleTableId
              );

              source = await prepareVariables({
                information: referenceTable.fields,
                variableType: lastVariable.variableType
                  ? lastVariable.variableType
                  : lastVariable.type
                ,
                modules: canvasDesignStore.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            }
          }

          this.content = this.content.replace(
            match[0],
            `<fts-i-unit contenteditable="false" field="${
              match[1]
            }">${commons.informationIconHtml(
              variableValues[variableValues.length - 1],
              this.$store.getters.workspaceModules
            )}&nbsp;${variableValues
              .map(x => `<span class="variable">${x.name}</span>`)
              .join(
                "<span class='path-indicator'>'s</span>&nbsp;"
              )}</fts-i-unit>`
          );
          match = re.exec(formula);
        }
      } catch (err) {
        console.warn(`FTSExpression error: ${err}`);
      }

      this.innerExpression = formula;
    }
  },
  watch: {
    formula(newVal) {
      if (!newVal) {
        this.content = "";
        this.innerExpression = "";
        this.$refs.formulaExpression.innerHTML = "";
      }
    },
    /* SS05134: updateDisplay rebuilds `content`, and `content` is bound with v-html on the
       contenteditable - so Vue replaces the whole editable subtree and the caret, which
       pointed into one of the replaced text nodes, falls back to offset 0. The For block
       dispatches automationDesignStore "updateVariables" from its own item watcher, so every
       `#` pick re-entered here and threw the caret to the start, which is what made a two-
       level-deep pick impossible.

       While the editor has focus the DOM is the source of truth: the user's chips are put
       there directly by selectItem/selectTemplate and updateContent has already emitted them
       upward, so this refresh has nothing to add and everything to lose. Defer it to the blur.

       Only this watcher is guarded. updateDisplay is also called directly (mounted, and the
       SS05836 display tests call it on a hand-made vm) and those callers do want the rebuild. */
    async variables(newVal) {
      if (this.editorHasFocus()) {
        this.displayRefreshPending = true;
        return;
      }
      await this.updateDisplay(this.formula);
    },
    /* SS06059: the node is in the document only while the menu is open, so exactly one
       .formula-fn-menu is ever reachable — the open one. A watcher (not the open call sites)
       because it must also cover a direct fnMenu.open mutation, and because user watchers
       flush BEFORE the re-render, so the node is on body and displayed by the time
       clampFnMenu measures it on the following tick. */
    "fnMenu.open"(open) {
      if (open) this.portalFnMenu();
      else this.unportalFnMenu();
    }
  },
  computed: {
    selection() {
      return this.variables;
    },
    /* A host that carries no variable scope at all is a query-filter operand editor: the
       listing / dashboard / list-view filter designers, which mount fts-query without
       variables (the automation designers always carry at least the trigger's Entry/Form).
       There are no fields to reference, so `#` offers constants only — and the placeholder
       says so rather than advertising fields that can never appear (SS05416). */
    filterContext() {
      return !this.computedField && !this.variables.length;
    },
    editorPlaceholder() {
      return this.filterContext
        ? "type a formula — # constants · Ctrl+Space browse"
        : "type a formula — # fields · Ctrl+Space browse";
    },
    /* Render model for the hint menu. Browse mode renders the groups openFnBrowse built
       (Fields · Constants · categories). The typed flow labels only the Fields block and
       keeps functions unlabeled — groups stay whole in the D6 order items already carry. */
    fnMenuGroups() {
      if (this.fnMenu.browse) return this.fnMenu.groups;
      const fields = this.fnMenu.items.filter(x => x.kind === "field");
      const functions = this.fnMenu.items.filter(x => x.kind !== "field");
      const groups = [];
      const fieldsGroup = { category: functions.length ? "Fields" : null, items: fields };
      const fnGroup = { category: null, items: functions };
      if (this.fnMenu.items[0] && this.fnMenu.items[0].kind === "field") {
        if (fields.length) groups.push(fieldsGroup);
        if (functions.length) groups.push(fnGroup);
      } else {
        if (functions.length) groups.push(fnGroup);
        if (fields.length) groups.push(fieldsGroup);
      }
      return groups;
    },
    /* Live diagnostics over the SERIALIZED formula (chips as {{…}} — the exact text the
       save-time lint sees). Full registry regardless of computed mode: unknown-function and
       arity truths are host-independent. Errors sort first. */
    diagnostics() {
      return diagnose(this.innerExpression || this.formula || "", this.fnRegistry || [],
        { excelSyntax: this.fieldFormula });
    },
    /* A field or column formula — the behavior ledger names the property, the computed-field
       editor drives the value formula — which the server reads with the Excel spellings (SUM,
       TRUE / FALSE, a leading "="). Automation, flow and action hosts pass neither: their
       expressions are read as written, so SUM there stays an unknown function. */
    fieldFormula() {
      return !!this.propertyName || this.computedField;
    },
    /* the strip now appears only when it has something to SAY (signature help / diagnostics);
       the lane verdict lives as the dot on the editor bar, so a clean formula has no strip */
    stripVisible() {
      return !!this.sigHelp || this.diagnostics.length > 0;
    },
    /* The signature split around the active parameter, e.g. TEXT + "(value, " + "format" + ")".
       Variadic signatures clamp onto their last parameter ("…"). */
    signatureParts() {
      if (!this.sigHelp) return null;
      const signature = this.sigHelp.fn.signature;
      const inner = signature.slice(
        this.sigHelp.fn.name.length + 1,
        signature.lastIndexOf(")"));
      const params = inner.split(",").map(x => x.trim());
      const active = Math.min(this.sigHelp.paramIndex, params.length - 1);
      const head = "(" + params.slice(0, active).map(p => p + ", ").join("");
      const tail = params.slice(active + 1).map(p => ", " + p).join("") + ")";
      return { head, active: params[active] || "", tail };
    },
    // the classify endpoint's rewrite for a "python" verdict, when it has one to offer
    standardRewrite() {
      const verdict = this.engineVerdict;
      return verdict && verdict.engine === "python" && verdict.rewrite && verdict.rewrite.formula
        ? verdict.rewrite
        : null;
    },
    engineBadgeLabel() {
      const labels = { client: "Client native", server: "Server native", python: "Python" };
      return (this.engineVerdict && labels[this.engineVerdict.engine]) || "";
    },
    engineBadgeTooltip() {
      if (!this.engineVerdict) return "";
      const base = {
        client: "Computes on-device (native bundle) — no round trip for this formula.",
        server: "Evaluates natively on the server — needs the sync round trip.",
        python: this.standardRewrite
          ? "Runs on the Python engine — use the standard syntax below to turn the activity native."
          : "Runs on the Python engine — rewrite to turn the activity native."
      }[this.engineVerdict.engine] || "";
      const reason = this.engineVerdict.reason ? ` ${this.engineVerdict.reason}.` : "";
      return `${base}${reason} This shows the formula's capability; the activity's server-native toggle decides the actual lane.`;
    }
  },
  async mounted() {
    // SS06059: the menu is portalled when it OPENS, not here — see the fnMenu.open watcher.
    // Closed, it stays out of the document, so only the open ledger is ever reachable.
    this.unportalFnMenu();
    window.addEventListener("scroll", this.onViewportChange, true);
    window.addEventListener("resize", this.onViewportChange);
    let vm = this;
    this.autofocus && this.focus();

    // function-hint registry: session-cached, so this resolves instantly after the first editor
    loadFormulaFunctions(action => canvasDesignStore.dispatch(action))
      .then(list => { vm.fnRegistry = list; });

    await this.updateDisplay(this.formula);

    // initial verdict for the formula the editor opened with
    if (this.engineBadge) this.classifyEngine();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onViewportChange, true);
    window.removeEventListener("resize", this.onViewportChange);
    // the portalled menu is no longer under the component root Vue is about to remove
    const menu = this.$refs.fnMenuEl;
    if (menu && menu.parentNode) menu.parentNode.removeChild(menu);
  }
};
</script>

<style lang="scss">
@use "sass:color";
@use "./src/assets/scss/colors" as *;

.fts-expression {
  position: relative; /* local stacking context for the editor bar (the hint menu is portalled to body) */
  color: var(--fts-form-control);
  cursor: text;
  word-break: break-word;
  overflow-wrap: anywhere;
  /* The editor bar (§3.2b): fx glyph + contenteditable in one quiet bordered shell —
     the input-shaped anchor every host shares. Focus tints the border and the glyph. */
  .formula-row {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    padding: 0.35rem 0.55rem;
    border: 1px solid var(--fts-gray-200, #dfe5ea);
    border-radius: 0.375rem;
    font-size: 0.8125rem; /* match the fx glyph — the bar stays quieter than its host */
    line-height: 1.5;
    transition: border-color 0.15s ease;
    > .formula-tribute,
    > div:not(.formula-fx) {
      flex: 1 1 auto;
      min-width: 0;
    }
    &:focus-within {
      border-color: var(--fts-primary);
      .formula-fx {
        color: var(--fts-primary);
      }
    }
  }
  .formula-fx {
    flex: 0 0 auto;
    padding-top: 1px;
    font-family: Georgia, "Times New Roman", serif;
    font-style: italic;
    font-weight: 700;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: var(--fts-form-control-placeholder);
    cursor: pointer;
    user-select: none;
    &:hover {
      color: var(--fts-primary);
    }
  }
  .formula {
    &:empty:before {
      content: attr(placeholder);
      -webkit-text-fill-color: var(--fts-form-control-placeholder);
    }
    fts-i-unit {
      background-color: var(--fts-formula-chip-bg, aliceblue);
      padding: 3px;
      margin: 2px;
      .information-icon {
        color: var(--fts-default);
        font-size: 90%;
      }
      .variable {
        color: var(--fts-formula-ref, #b26a00);
        font-weight: 600;
      }
      .path-indicator {
        color: var(--fts-default);
      }
    }
  }
  .editor-expression {
    margin-top: 10px;
    margin-bottom: 10px;
    font-size: 0.675rem;
    color: var(--fts-default);
  }
  /* The one status strip (§3.2b): fixed-height quiet footer — worst diagnostic or the
     active signature on the left, lane verdict right. Marks are the only colour. */
  .formula-strip,
  .formula-strip-open {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    margin-top: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
    border-radius: 0.25rem;
    color: var(--fts-default);
    background-color: var(--fts-gray-100);
    cursor: default;
    user-select: none;
  }
  .formula-strip {
    min-height: 1.5rem;
  }
  .formula-strip-open {
    margin-top: 0.15rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.15rem;
    .strip-row {
      display: flex;
      align-items: baseline;
      gap: 0.4rem;
    }
  }
  .strip-mark {
    font-weight: 700;
    font-size: 0.65rem;
    &.error { color: #c94f4f; }
    &.warn { color: #b58a1f; }
  }
  .strip-text {
    min-width: 0;
  }
  .strip-fix {
    font-family: Consolas, "Roboto Mono", monospace;
    font-size: 0.675rem;
    color: var(--fts-primary);
    text-decoration: underline dotted;
    text-underline-offset: 2px;
  }
  .strip-more {
    color: var(--fts-form-control-placeholder);
    white-space: nowrap;
    cursor: pointer;
  }
  .formula-rewrite {
    .formula-rewrite-text {
      min-width: 0;
      overflow-wrap: anywhere;
      text-decoration: none;
    }
    .formula-rewrite-use {
      color: var(--fts-primary);
    }
  }
  .strip-sig {
    font-family: Consolas, "Roboto Mono", monospace;
    font-size: 0.7rem;
    color: var(--fts-form-control);
    .strip-sig-name { font-weight: 650; }
    .strip-sig-on {
      text-decoration: underline;
      text-underline-offset: 3px;
      font-weight: 650;
    }
  }
  .strip-doc {
    color: var(--fts-form-control-placeholder);
  }
  /* lane verdict dot on the editor bar — words live in the tooltip */
  .formula-lane-dot {
    flex: 0 0 auto;
    align-self: center;
    width: 0.4rem;
    height: 0.4rem;
    margin-left: auto;
    border-radius: 50%;
    cursor: default;
    &.formula-lane-dot--client { background-color: #2e7d32; }
    &.formula-lane-dot--server { background-color: #1565c0; }
    &.formula-lane-dot--python { background-color: #b26a00; }
  }
  .strip-cheat {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    align-items: baseline;
    span {
      font-family: Consolas, "Roboto Mono", monospace;
      font-size: 0.65rem;
      background-color: var(--fts-popover-alt-background);
      border: 1px solid var(--fts-gray-100);
      border-radius: 0.2rem;
      padding: 0 0.35rem;
    }
    .strip-cheat-note {
      border: 0;
      background: none;
      font-family: inherit;
      color: var(--fts-form-control-placeholder);
    }
  }
}
/* function-hint menu: the calm ledger (docs/excel-formula-editor-proposal.md §3.2b) —
   aligned name | signature | description columns, grey wash selection, tier as a dot.
   No new hues: chips stay the only colour in the editor. */
.formula-fn-menu {
  position: fixed;
  z-index: 1070; /* over an append-to-body modal, which sets 1060 inline */
  min-width: 280px;
  max-width: 430px;
  max-height: 15rem;
  overflow: auto;
  padding: 0.25rem 0;
  font-size: 0.8125rem;
  background-color: var(--fts-popover-alt-background);
  border: 1px solid var(--fts-gray-100);
  border-radius: 0.375rem;
  box-shadow: var(--fts-elevation-3);
  .fn-cat {
    padding: 0.4rem 0.75rem 0.1rem;
    font-size: 0.625rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fts-form-control-placeholder);
  }
  .fn-item {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    cursor: pointer;
    &.sel,
    &:hover {
      background-color: var(--fts-gray-100);
    }
    .fn-name {
      min-width: 6.5rem;
      font-family: Consolas, "Roboto Mono", monospace;
      font-weight: 600;
      white-space: nowrap;
    }
    .fn-tierdot {
      display: inline-block;
      width: 5px;
      height: 5px;
      margin-left: 5px;
      vertical-align: 2px;
      border-radius: 50%;
      background-color: var(--fts-gray-300, #c3ccd2);
      &.client {
        background-color: #4a9e6f;
      }
      &.script {
        background-color: #b58a1f;
      }
    }
    .fn-chipname {
      display: inline-block;
      padding: 0 0.35rem;
      border-radius: 0.2rem;
      font-family: inherit;
      font-weight: 400;
      font-size: 0.75rem;
      background-color: var(--fts-formula-chip-bg, aliceblue);
      color: var(--fts-primary);
    }
    .fn-sig {
      font-family: Consolas, "Roboto Mono", monospace;
      font-size: 0.75rem;
      color: var(--fts-default);
      white-space: nowrap;
    }
    .fn-desc {
      margin-left: auto;
      font-size: 0.75rem;
      color: var(--fts-form-control-placeholder);
      text-align: right;
    }
  }
  .fn-foot {
    display: flex;
    justify-content: space-between;
    padding: 0.3rem 0.75rem 0.1rem;
    border-top: 1px solid var(--fts-gray-100);
    font-size: 0.6875rem;
    color: var(--fts-form-control-placeholder);
  }
}
/* dropdown menu */
.formula-dropdown-menu {
  min-width: 200px;
  padding: 0.375rem 0;
  overflow: auto;
  box-shadow: var(--fts-elevation-3);
  border-radius: 0.25rem;
  background-color: var(--fts-popover-alt-background);
  font-size: 1rem;
  > ul {
    margin: 0;
    background: inherit;
  }
  .formula-dropdown-option {
    display: flex;
    align-items: flex-start;
    padding: 0.375rem 0.75rem;
    background: inherit;
    &:hover {
      cursor: pointer;
    }
    &.highlight {
      background-color: var(--fts-gray-100);
    }
    .formula-option-icon {
      margin-top: 0.135rem;
      margin-right: 0.575rem;
      .default {
        color: var(--fts-primary);
      }
    }
    .formula-option-label {
      flex: 1;
      min-height: calc(1rem + 0.135rem + 0.135rem);
    }
    .formula-option-tag {
      @include theme() {
        color: color.adjust(theme-get("fts-primary"), $lightness: 45%);
      }
      margin-left: 0.575rem;
      padding: calc(0.135rem + 1px) 0.275rem;
      font-size: calc(1rem - 0.135rem - 0.135rem);
      border-radius: 0.25rem;
      background-color: var(--fts-primary);
      &.system {
        //
      }
      &.variable {
        //
      }
    }
  }
}
</style>